import { Button, Drawer, Stack } from '@mui/material'
import { useToast } from '@repo/design-system/index'
import { useEffect, useState } from 'react'
import apis from '../../../../modules/apis'
import { usePersonas } from '../../../../modules/apis/ai_tutor/persona'
import { axiosGet, axiosPost } from '../../../../modules/axiosHelper'
import { n_ai_tutor, n_common } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { usePersonaStore } from '../../../../store/usePersonaStore'
import { Persona } from '../../../../types/domain/persona'
import { camelizeKeys, convertKeysToSnakeCase } from '../../../../utils/keyValue'
import AdditionalInfo from './with_whom/AdditionalInfo'
import BioInfo from './with_whom/BioInfo'
import ExperienceDurationSelect from './with_whom/ExperienceDurationSelect'
import Name from './with_whom/Name'
import Personas from './with_whom/Personas'
import Relationship from './with_whom/Relationship'
import Tone from './with_whom/Tone'
import WhatTheyDo from './with_whom/WhatTheyDo'

const WithWhom = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState(0)
  const length = 7

  // we need to fetch personas!
  // const { data: personas = [], refetch } = useQuery('personas', fetchPersonas);
  const { data } = usePersonas()
  const personas = usePersonaStore((s) => s.personas)
  const setPersonas = usePersonaStore((s) => s.setPersonas)
  const { locale, t } = useTranslation()

  const { toastSuccess } = useToast()

  useEffect(() => {
    if (data) {
      const normalizedPersonas = data.personas.map((p) => ({
        id: p.id,
        ...camelizeKeys(p.data),
        user_id: p.user_id,
      }))

      setPersonas(normalizedPersonas) // or whatever shape your data has
    }
  }, [data, setPersonas])

  const {
    jobCategoryId,
    setJobCategories,
    companyTypeId,
    toneId,
    relationshipId,
    intimacyId,
    ageId,
    pronunciationId,
    genderId,
    experienceDurationId,
    additionalInfo,
    name,
  } = usePersonaStore()

  const addPersona = (newPersona: Persona) => {
    setPersonas([...personas, newPersona])
  }

  const onPrev = () => {
    if (step === 0) {
      return
    }
    setStep((prev) => prev - 1)
  }

  const fetchJobCategories = async () => {
    const data = await axiosGet(apis.aiTutor.job.getJobCategories())
    console.log('job categores =====>', data)
    setJobCategories(data)
  }

  useEffect(() => {
    fetchJobCategories()
  }, [])

  const onNext = async () => {
    if (length - 1 === step) {
      //🟡 [TODO] api~
      //read all states
      const payload = {
        jobCategoryId: jobCategoryId,
        companyTypeId: companyTypeId,
        toneId: toneId,
        relationshipId: relationshipId,
        intimacyId: intimacyId,
        ageId: ageId,
        pronunciationId: pronunciationId,
        genderId: genderId,
        experienceDurationId: experienceDurationId,
        additionalInfo: additionalInfo,
        name: name,
      }

      const snakeCasePayload = convertKeysToSnakeCase(payload)

      const data = await axiosPost(apis.aiTutor.persona.create(), {
        data: snakeCasePayload,
      })

      // const serverPersona = data.persona
      const camelizedData = camelizeKeys(data.persona.data) as Omit<Persona, 'id'>

      const convertedServerPersona: Persona = {
        id: data.persona.id,
        ...camelizedData,
      }

      setIsOpen(false)

      console.log('conertedserverperonsa ====>', convertedServerPersona)
      addPersona(convertedServerPersona)

      toastSuccess('Successfully Created')

      return
    }

    setStep((prev) => prev + 1)
  }

  const validateStep = (step: number) => {
    switch (step) {
      case 0:
        return !!jobCategoryId && !!companyTypeId
      case 1:
        return !!experienceDurationId
      case 2:
        return !!relationshipId && !!intimacyId // 2단계: 추가 입력 필요 없으면 항상 통과
      case 3:
        return !!toneId // 마지막 저장 단계
      case 4:
        return !!ageId && !!genderId && !!pronunciationId
      case 5:
        return true
      case 6:
        return name
      default:
        return false
    }
  }

  const isNextDisabled = !validateStep(step)
  // const isPrevDisabled = step === 0

  return (
    <div style={{ position: 'relative', height: '100%', display: 'flex' }} className="flex-col ">
      <div className="p-[20px]">
        <span className="Headline3-22Bd">{t(n_ai_tutor.custom_roleplay.step1_title_1)}</span>
      </div>

      <div style={{ width: '100%' }} className="flex justify-center items-center flex-1 flex-col pb-[120px]">
        <Personas personas={personas} />
      </div>

      {/* <div
        className="flex justify-center items-center mt-[40px]"
        style={{ position: 'absolute', bottom: 80, left: 0, right: 0 }}
      >
        <div style={{ cursor: 'pointer' }} className="text-purple-500 Body2-14Md" onClick={(e) => setIsOpen(true)}>
          새로운 상대 생성하기{' '}
        </div>
      </div>
      <br /> */}

      <Drawer
        open={isOpen}
        anchor={'bottom'}
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
        onClose={(_e) => setIsOpen(false)}
      >
        <div
          style={{
            height: '90vh', // ✅ works as intended
            maxHeight: '90%',
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            backgroundColor: 'white',
            overflow: 'hidden',
          }}
        >
          <div style={{ backgroundColor: '', height: 32 }}></div>

          {step === 0 && <WhatTheyDo />}
          {step === 1 && <ExperienceDurationSelect />}
          {step === 2 && <Relationship />}
          {step === 3 && <Tone />}
          {step === 4 && <BioInfo />}
          {step === 5 && <AdditionalInfo />}
          {step === 6 && <Name />}

          <Stack
            spacing={2}
            direction="row"
            justifyContent="space-between"
            sx={{
              position: 'absolute',
              bottom: `calc(env(safe-area-inset-bottom) + 16px)`, // ← 여기!
              left: 0,
              width: '100%',
              px: 2,
            }}
          >
            {step !== 0 && (
              <Button
                variant="outlined"
                className="!border-purple-600 text-purple-600"
                style={{ borderWidth: 1, borderRadius: 8, height: 58 }}
                onClick={onPrev}
                sx={{ flex: 1 }}
              >
                {t(n_common.btn_back)}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={onNext}
              sx={{
                flex: 1,

                backgroundColor: '#3C2BAC',
                color: '#fff', // ← 글자색 흰색
                '&:hover': {
                  backgroundColor: '#34218f',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#ccc',
                  color: '#666', // 비활성화 시 글자색도 회색 계열로
                },
              }}
              style={{ height: 58, borderRadius: 8 }}
              disabled={isNextDisabled}
            >
              {step === length - 1 ? 'Create Persona' : t(n_common.btn_next)}
            </Button>
          </Stack>
        </div>
      </Drawer>
    </div>
  )
}

export default WithWhom
